import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type {
  ProfileAlert,
  ProfileKeyworker,
  ServiceRef,
  SubdomainAssessmentRow,
  SessionSummary,
  WhanauMember,
} from "../api/client";
import { formatDate } from "../api/formatDate";
import "./ProfileTiles.css";

/**
 * Whānau-profile tile grid + drilldown modals.
 *
 * The profile page is composed of four tiles — Members, Case Workers,
 * Assessments, Factors — each showing a count + last-activity summary.
 * Clicking a tile opens a modal whose body is supplied by the parent
 * (so the parent retains all editor state / save bars / etc.).
 *
 * This component owns:
 *   - the four tile cards (presentation only)
 *   - the modal shell (portal, backdrop, ESC handling, close button)
 *
 * The parent owns:
 *   - which modal is open (`open` prop)
 *   - the modal's body content (`modalBody` prop)
 *   - actually closing the modal (`onClose` callback)
 */

export type TileKey =
  | "members"
  | "services"
  | "caseworkers"
  | "assessments"
  | "factors"
  | "recommendations"
  | null;

export type ProfileTilesProps = {
  members: WhanauMember[];
  /** Every service the whānau is attached to (whanau_service). */
  services: ServiceRef[];
  keyworkers: ProfileKeyworker[];
  sessions: SessionSummary[];
  assessments: SubdomainAssessmentRow[];
  alerts: ProfileAlert[];
  /** Open (not-yet-actioned) recommendations — drives the tile count. */
  recommendationOpenCount: number;
  /** Open urgent recommendations — drives the tile's warning badge. */
  recommendationUrgentCount: number;
  onTileClick: (which: Exclude<TileKey, null>) => void;
};

export function ProfileTiles({
  members,
  services,
  keyworkers,
  sessions,
  assessments,
  alerts,
  recommendationOpenCount,
  recommendationUrgentCount,
  onTileClick,
}: ProfileTilesProps) {
  const { t } = useTranslation();

  const memberCount = members.length;
  const keyworkerCount = keyworkers.length;
  const sessionCount = sessions.length;
  const lastSessionAt = sessions[0]?.session_at ?? null;

  // Factor counts by state.
  const counts = { risk: 0, change: 0, protective: 0, notAssessed: 0 };
  for (const a of assessments) {
    if (a.state_kind === "risk") counts.risk++;
    else if (a.state_kind === "change") counts.change++;
    else if (a.state_kind === "protective") counts.protective++;
    else counts.notAssessed++;
  }

  return (
    <div className="profile-tiles">
      <TileCard
        title={t("profileTiles.members.title")}
        count={memberCount}
        meta={
          memberCount === 0
            ? t("profileTiles.members.empty")
            : t("profileTiles.members.count", { count: memberCount })
        }
        onClick={() => onTileClick("members")}
      />

      <TileCard
        title={t("profileTiles.services.title")}
        count={services.length}
        meta={
          services.length === 0
            ? t("profileTiles.services.empty")
            : services.map((s) => s.name_en).join(", ")
        }
        onClick={() => onTileClick("services")}
      />

      <TileCard
        title={t("profileTiles.caseworkers.title")}
        count={keyworkerCount}
        meta={
          keyworkerCount === 0
            ? t("profileTiles.caseworkers.empty")
            : keyworkerCount === 1
              ? (keyworkers[0]?.display_name ?? keyworkers[0]?.upn ?? "")
              : t("profileTiles.caseworkers.count", { count: keyworkerCount })
        }
        onClick={() => onTileClick("caseworkers")}
      />

      <TileCard
        title={t("profileTiles.assessments.title")}
        count={sessionCount}
        meta={
          lastSessionAt
            ? t("profileTiles.assessments.lastOn", {
                date: formatDate(lastSessionAt),
              })
            : t("profileTiles.assessments.empty")
        }
        onClick={() => onTileClick("assessments")}
      />

      <TileCard
        title={t("profileTiles.factors.title")}
        meta={
          <span className="profile-tile__factor-summary">
            <span className="profile-tile__factor profile-tile__factor--risk">
              {t("profileTiles.factors.risk", { count: counts.risk })}
            </span>
            <span className="profile-tile__factor profile-tile__factor--change">
              {t("profileTiles.factors.change", { count: counts.change })}
            </span>
            <span className="profile-tile__factor profile-tile__factor--protective">
              {t("profileTiles.factors.protective", {
                count: counts.protective,
              })}
            </span>
            {counts.notAssessed > 0 && (
              <span className="profile-tile__factor profile-tile__factor--not-assessed">
                {t("profileTiles.factors.notAssessed", {
                  count: counts.notAssessed,
                })}
              </span>
            )}
          </span>
        }
        badge={
          alerts.length > 0
            ? t("profileTiles.factors.alertCount", { count: alerts.length })
            : null
        }
        onClick={() => onTileClick("factors")}
      />

      <TileCard
        title={t("profileTiles.recommendations.title")}
        count={recommendationOpenCount}
        meta={
          recommendationOpenCount === 0
            ? t("profileTiles.recommendations.empty")
            : t("profileTiles.recommendations.open", {
                count: recommendationOpenCount,
              })
        }
        badge={
          recommendationUrgentCount > 0
            ? t("profileTiles.recommendations.urgentBadge", {
                count: recommendationUrgentCount,
              })
            : null
        }
        onClick={() => onTileClick("recommendations")}
      />
    </div>
  );
}

// ============================================================================
// One tile card
// ============================================================================

function TileCard({
  title,
  count,
  meta,
  badge,
  onClick,
}: {
  title: string;
  count?: number;
  meta: ReactNode;
  badge?: string | null;
  onClick: () => void;
}) {
  return (
    <button type="button" className="profile-tile" onClick={onClick}>
      <span className="profile-tile__title">
        {title}
        {typeof count === "number" && (
          <span className="profile-tile__count">{count}</span>
        )}
        {badge && <span className="profile-tile__badge">{badge}</span>}
      </span>
      <span className="profile-tile__meta">{meta}</span>
    </button>
  );
}

// ============================================================================
// Modal shell — used by the parent to render tile detail.
// ============================================================================

export function ProfileTileModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="profile-tile-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="profile-tile-modal" role="dialog" aria-modal="true">
        <header className="profile-tile-modal__header">
          <h2 className="profile-tile-modal__title">{title}</h2>
          <button
            type="button"
            className="profile-tile-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="profile-tile-modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ============================================================================
// Case Workers tile body — list view used inside the tile modal.
// ============================================================================

export function CaseworkersList({
  keyworkers,
}: {
  keyworkers: ProfileKeyworker[];
}) {
  const { t } = useTranslation();
  if (keyworkers.length === 0) {
    return (
      <p className="profile-tile-modal__empty">
        {t("profileTiles.caseworkers.empty")}
      </p>
    );
  }
  return (
    <ul className="profile-caseworkers">
      {keyworkers.map((k) => (
        <li key={k.kaimahi_oid} className="profile-caseworker">
          <strong className="profile-caseworker__name">
            {k.display_name ?? k.upn}
          </strong>
          {k.display_name && (
            <span className="profile-caseworker__upn">{k.upn}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ============================================================================
// Services tile body — every service the whānau is attached to.
// ============================================================================

export function ServicesList({ services }: { services: ServiceRef[] }) {
  const { t } = useTranslation();
  if (services.length === 0) {
    return (
      <p className="profile-tile-modal__empty">
        {t("profileTiles.services.empty")}
      </p>
    );
  }
  return (
    <ul className="profile-services">
      {services.map((s) => (
        <li key={s.service_id} className="profile-service">
          <span className="profile-service__name">{s.name_en}</span>
        </li>
      ))}
    </ul>
  );
}

// ============================================================================
// Profile header — buttons LEFT of the whānau name.
// ============================================================================

export function ProfileHeaderTile({
  name,
  metaRows,
  onStartAssessment,
  startAssessmentDisabled,
  startAssessmentDisabledHint,
  onShare,
  shareLabel,
  shareDisabled,
  startAssessmentLabel,
}: {
  name: string;
  metaRows: Array<{ label: string; value: ReactNode }>;
  onStartAssessment: () => void;
  /** When true, the Start an assessment button is disabled. */
  startAssessmentDisabled?: boolean;
  /** Tooltip / aria-description explaining why it's disabled. */
  startAssessmentDisabledHint?: string;
  onShare: () => void;
  shareLabel: string;
  shareDisabled?: boolean;
  startAssessmentLabel: string;
}) {
  return (
    <header className="profile-header">
      <div className="profile-header__actions">
        <button
          type="button"
          className="profile-header__action profile-header__action--primary"
          onClick={onStartAssessment}
          disabled={startAssessmentDisabled}
          title={
            startAssessmentDisabled ? startAssessmentDisabledHint : undefined
          }
        >
          {startAssessmentLabel}
        </button>
        <button
          type="button"
          className="profile-header__action profile-header__action--secondary"
          onClick={onShare}
          disabled={shareDisabled}
        >
          {shareLabel}
        </button>
      </div>
      <div className="profile-header__identity">
        <h2 id="profile-title" className="profile-header__name">
          {name}
        </h2>
        <dl className="profile-header__meta">
          {metaRows.map((row, i) => (
            <div key={i} className="profile-header__meta-row">
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </header>
  );
}

// ============================================================================
// Factors read-only list — grouped by domain, each subdomain shows current
// state + when recorded + change-since-last. Used inside the Factors tile
// modal in place of the editable FactorGrid.
// ============================================================================

import { StateIcon } from "./StateIcon";
import { useFramework, frameworkLabel } from "../framework";

export function FactorsReadOnlyList({
  assessments,
}: {
  assessments: SubdomainAssessmentRow[];
}) {
  const { t, i18n } = useTranslation();
  const { domains, isHidden } = useFramework();

  // Group assessments by domain in framework-canonical order. Hidden
  // subdomains: if they have history (state_kind populated) we still show
  // the row with a "Retired" chip; if they have no history we drop them.
  const byDomain = new Map<string, SubdomainAssessmentRow[]>();
  for (const d of domains) byDomain.set(d.id, []);
  for (const a of assessments) {
    const hidden = isHidden("subdomain", a.subdomain_id);
    if (hidden && !a.state_kind) continue; // hidden + no history → drop
    const list = byDomain.get(a.domain_id);
    if (list) list.push(a);
  }

  return (
    <div className="factors-readonly">
      {domains.map((domain) => {
        const rows = byDomain.get(domain.id) ?? [];
        if (rows.length === 0) return null;
        return (
          <section key={domain.id} className="factors-readonly__domain">
            <h3 className="factors-readonly__domain-title">
              {frameworkLabel(domain, "domain", i18n.language, t)}
            </h3>
            <ul className="factors-readonly__rows">
              {rows
                .slice()
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((a) => (
                  <FactorReadOnlyRow
                    key={a.subdomain_id}
                    row={a}
                    isRetired={isHidden("subdomain", a.subdomain_id)}
                  />
                ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function FactorReadOnlyRow({
  row,
  isRetired,
}: {
  row: SubdomainAssessmentRow;
  isRetired: boolean;
}) {
  const { t, i18n } = useTranslation();
  // Resolve display name via the merged framework — admin renames apply here.
  const { findSubdomain } = useFramework();
  const merged = findSubdomain(row.subdomain_id);
  const displayName = merged
    ? frameworkLabel(merged, "subdomain", i18n.language, t)
    : row.name_en;

  const state = row.state_kind;
  const previous = row.previous_state_kind;
  const hasNotes = !!(row.kaimahi_notes || row.shared_notes);

  const stateLabel = (kind: typeof state) =>
    !kind
      ? t("profileTiles.factors.notRecorded")
      : t(
          `framework.state.${kind === "not-assessed" ? "notAssessed" : kind}.descriptor`,
        );

  return (
    <li
      className={`factors-readonly__row${isRetired ? " factors-readonly__row--retired" : ""}`}
    >
      <div className="factors-readonly__row-head">
        <span className="factors-readonly__name">{displayName}</span>
        {isRetired && (
          <span
            className="factors-readonly__retired"
            aria-label={t("profileTiles.factors.retired")}
            title={t("profileTiles.factors.retiredHint")}
          >
            {t("profileTiles.factors.retired")}
          </span>
        )}
        {hasNotes && (
          <span
            className="factors-readonly__notes-flag"
            aria-label={t("profileTiles.factors.hasNotes")}
            title={t("profileTiles.factors.hasNotes")}
          >
            ●
          </span>
        )}
      </div>
      <div
        className={`factors-readonly__current factors-readonly__current--${state ?? "none"}`}
      >
        <span
          className={`factors-readonly__pill factors-readonly__pill--${state ?? "none"}`}
        >
          {state && (
            <StateIcon state={state} className="factors-readonly__icon" />
          )}
          <span className="factors-readonly__pill-text">
            <span className="factors-readonly__pill-en">
              {stateLabel(state)}
            </span>
            {state && state !== "not-assessed" && (
              <span className="factors-readonly__pill-mi" lang="mi">
                {t(`framework.state.${state}.teReo`)}
              </span>
            )}
          </span>
        </span>
        {row.recorded_at && (
          <span className="factors-readonly__recorded">
            {t("profileTiles.factors.recordedOn", {
              date: formatDate(row.recorded_at),
            })}
          </span>
        )}
      </div>
      {previous && state && previous !== state && (
        <div className="factors-readonly__change">
          <span
            className={`factors-readonly__change-icon factors-readonly__change-icon--${getDirection(previous, state)}`}
            aria-hidden="true"
          >
            {getDirection(previous, state) === "up"
              ? "↑"
              : getDirection(previous, state) === "down"
                ? "↓"
                : "→"}
          </span>
          {t("profileTiles.factors.changedFrom", {
            from: stateLabel(previous),
            since: row.previous_recorded_at
              ? formatDate(row.previous_recorded_at)
              : "?",
          })}
        </div>
      )}
      {previous && state && previous === state && (
        <div className="factors-readonly__nochange">
          {t("profileTiles.factors.unchangedSince", {
            since: row.previous_recorded_at
              ? formatDate(row.previous_recorded_at)
              : "?",
          })}
        </div>
      )}
    </li>
  );
}

/** Coarse direction: risk(1) → change(2) → protective(3). not-assessed is neutral. */
function getDirection(
  from: "risk" | "change" | "protective" | "not-assessed",
  to: "risk" | "change" | "protective" | "not-assessed",
): "up" | "down" | "flat" {
  const rank: Record<string, number> = {
    risk: 1,
    change: 2,
    protective: 3,
    "not-assessed": 0,
  };
  const a = rank[from] ?? 0;
  const b = rank[to] ?? 0;
  if (b > a) return "up";
  if (b < a) return "down";
  return "flat";
}
